<template>
  <v-app>
    <div class="hero-header">
      <div class="hero-content">
        <div class="hero-flex">
          <div>
            <h1 class="hero-title">
              <v-icon dark large class="mr-2" style="opacity: 0.85">mdi-bookshelf</v-icon>
              ข้อมูลบุคลากรจำแนกตามสาขาวิชา
            </h1>
            <p class="hero-subtitle">
              ระบบแสดงข้อมูลสารสนเทศสาธารณะ (Public Data) คุ้มครองข้อมูลส่วนบุคคลตามหลัก PDPA
            </p>
            <div class="hero-accent-bar"></div>
          </div>
          <div class="hero-actions">
            <v-btn
              color="success"
              dark
              depressed
              @click="exportToExcel"
              :disabled="personnelList.length === 0"
            >
              <v-icon small left>mdi-file-excel</v-icon> ส่งออก Excel (.xlsx)
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-container fluid class="mt-4">
      
      <v-card class="elevation-2 mb-6" style="border-radius: 16px;">
        <v-card-text class="pa-6">
          <v-row align="center">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="searchKeyword"
                label="ระบุ ประเภทวิชา, สาขาวิชา หรือ ชื่อบุคลากร เพื่อค้นหา..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                @keyup.enter="doSearch"
                @click:clear="clearSearch"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="d-flex">
              <v-btn 
                color="#263238" 
                dark 
                depressed 
                height="40" 
                class="flex-grow-1 mr-2" 
                @click="doSearch" 
                :loading="loading"
              >
                <v-icon left>mdi-magnify</v-icon> ค้นหาข้อมูล
              </v-btn>
              <v-btn 
                outlined 
                color="grey" 
                depressed 
                height="40" 
                @click="clearSearch" 
                :disabled="!hasSearched"
              >
                <v-icon left>mdi-refresh</v-icon> ล้าง
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card v-if="!hasSearched" class="elevation-0 text-center py-12" style="background: transparent;">
        <v-icon size="80" color="#90A4AE">mdi-account-search-outline</v-icon>
        <h2 class="mt-4" style="color: #263238;">ค้นหาและจัดกลุ่มข้อมูลบุคลากร</h2>
        <p class="grey--text mt-2">
          พิมพ์ <strong>ประเภทวิชา</strong> หรือ <strong>สาขาวิชา</strong> (เช่น ช่างยนต์, บัญชี, เทคโนโลยีสารสนเทศ) <br> 
          เพื่อเรียกดูรายชื่อบุคลากรและส่งออกเป็นไฟล์ Excel
        </p>
      </v-card>

      <v-card v-else class="elevation-2" style="border-radius: 16px;">
        <v-card-title class="py-4 px-6 d-flex align-center" style="background-color: #F5F5F5; border-bottom: 1px solid #E4E4E7;">
          <v-icon color="#A86400" class="mr-2">mdi-check-circle</v-icon>
          <span class="font-weight-bold" style="color: #263238;">
            พบข้อมูล {{ personnelList.length }} รายการ (จัดกลุ่มตามประเภทวิชา)
          </span>
          <v-spacer></v-spacer>
          
          <v-text-field
            v-model="tableSearch"
            append-icon="mdi-filter"
            label="กรองข้อมูลในตาราง"
            single-line
            hide-details
            outlined
            dense
            style="max-width: 300px;"
            class="bg-white"
          ></v-text-field>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="personnelList"
          :search="tableSearch"
          :loading="loading"
          group-by="subject_type_name"
          class="custom-group-table"
          :items-per-page="20"
          no-data-text="ไม่พบข้อมูลบุคลากรที่ตรงกับคำค้นหา"
        >
          <template v-slot:group.header="{ group, items, isOpen, toggle }">
            <td :colspan="headers.length" class="group-header-row" @click="toggle">
              <v-icon :color="isOpen ? 'primary' : 'grey'" class="mr-2">
                {{ isOpen ? 'mdi-minus-circle' : 'mdi-plus-circle' }}
              </v-icon>
              <strong>ประเภทวิชา: {{ group }}</strong>
              <v-chip small color="primary" class="ml-3 px-3 font-weight-bold" dark>
                {{ items.length }} คน
              </v-chip>
            </td>
          </template>    

          <template v-slot:item.user_status_name="{ item }">
            <v-chip small outlined :color="getTypeColor(item.user_status_name)">
              {{ getTypeName(item.user_status_name) }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import * as XLSX from "xlsx";

const TYPE_MAP = {
  director: { name: "ผู้อำนวยการ", color: "#263238" },
  se_director: { name: "รองผู้อำนวยการ", color: "#455A64" },
  teacher: { name: "ข้าราชการครู", color: "#455A64" },
  supervision: { name: "ศึกษานิเทศก์", color: "#B3151B" },
  "38": { name: "บุคลากร 38 ค.(2)", color: "#2E7D32" },
  permanent: { name: "พนักงานราชการ", color: "#2E7D32" },
  governmentteach: { name: "ครูพนักงานราชการ", color: "#A86400" },
  temporary: { name: "ครูอัตราจ้าง", color: "#A86400" }
};

export default {
  name: "PersonnelPublicGroup",
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      hasSearched: false, // เช็คว่าเคยกดค้นหาหรือยัง
      searchKeyword: "",  // คำค้นหาสำหรับส่งไป API
      tableSearch: "",    // คำค้นหาสำหรับกรองในตาราง
      personnelList: [],
      snackbar: { show: false, text: "", color: "success" },
      headers: [
        { text: "คำนำหน้า", value: "personnel_etc_title", width: "100px" },
        { text: "ชื่อ", value: "personnel_etc_fristname", width: "180px" },
        { text: "นามสกุล", value: "personnel_etc_lastname", width: "180px" },
        { text: "สาขาวิชา", value: "branch_name_th", width: "200px" },
        { text: "ประเภท", value: "user_status_name", width: "150px" },
        { text: "ตำแหน่ง/วิทยฐานะ", value: "personnel_etc_rang", width: "180px" },
        { text: "สถานศึกษา", value: "college_name", width: "220px" },
        { text: "จังหวัด", value: "province_name", width: "120px" }
      ]
    };
  },
  // ลบ this.fetchData() ออกจาก mounted() เพื่อไม่ให้ดึงข้อมูลอัตโนมัติ
  mounted() {},
  methods: {
    async doSearch() {
      if (!this.searchKeyword || this.searchKeyword.trim().length < 2) {
        this.showSnack("กรุณาระบุคำค้นหาอย่างน้อย 2 ตัวอักษร", "warning");
        return;
      }

      this.loading = true;
      try {
        const resp = await this.$http.post("personnel_public_group.php", {
          ApiKey: this.ApiKey,
          search_keyword: this.searchKeyword.trim()
        });

        if (resp.data.status === "success") {
          this.personnelList = resp.data.data || [];
          this.hasSearched = true;
          this.tableSearch = ""; // ล้างช่องกรองในตาราง
        } else {
          this.showSnack(resp.data.message || "ไม่พบข้อมูล", "warning");
          this.personnelList = [];
        }
      } catch (err) {
        this.showSnack("การเชื่อมต่อ API ล้มเหลว", "error");
      }
      this.loading = false;
    },

    clearSearch() {
      this.searchKeyword = "";
      this.tableSearch = "";
      this.personnelList = [];
      this.hasSearched = false;
    },

    getTypeName(type) {
      return TYPE_MAP[type] ? TYPE_MAP[type].name : type || "-";
    },
    
    getTypeColor(type) {
      return TYPE_MAP[type] ? TYPE_MAP[type].color : "#95A5A6";
    },

    showSnack(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    exportToExcel() {
      if (this.personnelList.length === 0) return;

      const exportData = this.personnelList.map((item, index) => ({
        "ลำดับ": index + 1,
        "ประเภทวิชา": item.subject_type_name,
        "สาขาวิชา": item.branch_name_th,       
        "คำนำหน้า": item.personnel_etc_title,
        "ชื่อ": item.personnel_etc_fristname,
        "นามสกุล": item.personnel_etc_lastname,
        "ประเภทบุคลากร": this.getTypeName(item.user_status_name),
        "ตำแหน่ง": item.personnel_etc_rang || "-",
        "วิทยฐานะ": item.personnel_etc_level || "-",
        "สถานศึกษา": item.college_name || "-",
        "จังหวัด": item.province_name || "-"
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const wscols = [
        { wch: 8 }, { wch: 20 }, { wch: 30 }, { wch: 10 }, 
        { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 25 }, 
        { wch: 15 }, { wch: 30 }, { wch: 15 }
      ];
      worksheet['!cols'] = wscols;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "ข้อมูลบุคลากร");

      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `Public_Personnel_BySubject_${dateStr}.xlsx`);
      
      this.showSnack("ส่งออกไฟล์ Excel สำเร็จ");
    }
  }
};
</script>

<style scoped>
.hero-header {
  background: linear-gradient(135deg, #263238 0%, #455a64 60%, #455a64 100%);
  position: relative;
  overflow: hidden;
}
.hero-header::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(232, 145, 58, 0.15) 0%, transparent 70%);
  border-radius: 50%;
}
.hero-content {
  position: relative;
  z-index: 2;
  padding: 32px 40px 28px;
}
.hero-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.hero-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px;
}
.hero-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
}
.hero-accent-bar {
  width: 48px;
  height: 3px;
  background: #a86400;
  border-radius: 2px;
  margin: 12px 0 0;
}
.hero-actions {
  display: flex;
  gap: 8px;
}
.group-header-row {
  background-color: #F5F5F5 !important;
  cursor: pointer;
  transition: background-color 0.2s;
  border-top: 2px solid #E4E4E7;
}
.group-header-row:hover {
  background-color: #F5F5F5 !important;
}
.group-header-row strong {
  font-size: 15px;
  color: #263238;
}
.custom-group-table >>> tbody tr td {
  border-bottom: 1px solid #F5F5F5;
}
.bg-white {
  background-color: white !important;
  border-radius: 4px;
}
</style>